import {
    IntegrationConfigField,
    IntegrationModal,
    IntegrationsSubSection,
    IntegrationSubSectionRecord,
    IntegrationTabStructure,
} from '../../interface/integrationmodal.interface';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { RecordData } from './integration-record-data.class';

export class IntegrationRecord {
    appId?: string;

    configuredData?: string;

    id?: string;

    name?: string;

    organizationId?: string;

    type?: string;

    tableValues?: { [property: string]: { [property: string]: string | boolean | string[] } };

    recordData?: IntegrationModal;

    originalData?: { [property: string]: any };

    subOrganizationId?: string;

    integrationMethod?: string;

    isValid?: boolean;

    active?: boolean;

    createdByName?: string;

    createdBy?: string;

    createdOn?: string;

    lastUpdatedByName?: string;

    lastUpdatedBy?: string;

    lastUpdatedOn?: string;

    constructor(record: IntegrationRecord, metaData: IntegrationModal) {
        if (!record) {
            return;
        }
        const keysToRemove = ['integrationSettings'];
        keysToRemove.forEach(key => {
            if ((record as Object).hasOwnProperty(key)) {
                delete record[key];
            }
        });
        this.appId = record?.appId;
        this.configuredData = record?.configuredData;
        this.originalData =
            (record?.originalData && CommonUtilsService.cloneObject(record.originalData)) || CommonUtilsService.cloneObject(record);
        this.originalData['configuredData'] = this.configuredData ? JSON.parse(this.configuredData) : {};
        this.id = record?.id;
        this.name = record?.name;
        this.organizationId = record?.organizationId;
        this.type = record?.type || metaData.api.toLowerCase();
        // tslint:disable-next-line: no-use-before-declare
        this.recordData = new RecordData(this.originalData as any, metaData);
        this.subOrganizationId = record?.subOrganizationId;
        this.integrationMethod = record?.integrationMethod;
        this.isValid = record?.isValid !== undefined ? record.isValid : false;
        this.active = record?.active;
        this.createdByName = record?.createdByName;
        this.createdBy = record?.createdBy;
        this.createdOn = record?.createdOn;
        this.lastUpdatedByName = record?.lastUpdatedByName;
        this.lastUpdatedBy = record?.lastUpdatedBy;
        this.lastUpdatedOn = record?.lastUpdatedOn;

        this.tableValues = {};
        this.recordData.tableFieldIds?.forEach(fieldId => {
            const field = this.recordData.fields.find(dataField => dataField.id === fieldId);
            if (field) {
                this.tableValues[fieldId] = {
                    displayName: field.displayName,
                    id: fieldId,
                    value: field.value,
                };
            } else if (!field && this.recordData.subSections) {
                this.recordData.subSections.forEach(subFieldObject => {
                    let subField: IntegrationConfigField;
                    for (let i = 0; i < subFieldObject.tabs.length; i++) {
                        const tab = subFieldObject.tabs[i];
                        subField = tab.fields.find(metaDataField => metaDataField.id === fieldId);
                        if (subField) {
                            break;
                        }
                    }
                    if (subField) {
                        this.tableValues[fieldId] = {
                            displayName: subField.displayName,
                            id: fieldId,
                            value: subField.value,
                        };
                    }
                });
            }
        });
    }

    addNewTabRecord? = (tab: IntegrationTabStructure) => {
        if (!tab.records) {
            tab.records = [];
        }
        if (tab.array) {
            const newRecord: IntegrationTabStructure['records'][0] = {
                fields: [],
                recordActions: [],
            };
            tab.fields.forEach(tabField => {
                const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                newTabField.value = newTabField.defaultValue;
                newRecord.fields.push(newTabField);
            });
            tab.recordActions?.forEach(tabField => {
                const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                newTabField.value = newTabField.defaultValue;
                newRecord.recordActions.push(newTabField);
            });
            tab.records.push(newRecord);
        }
    };

    getRecordField? = (fieldId: string, tab?: IntegrationTabStructure, _subSectionRecord?: any): IntegrationConfigField => {
        return this.getRecordFieldByProperty('id', fieldId, tab);
    };

    getRecordFieldParentObject? = (fieldId: string, tab?: IntegrationTabStructure, _subSectionRecord?: any): IntegrationConfigField => {
        return this.getRecordFieldParentObjectByProperty('id', fieldId, tab);
    };

    setRecordFieldValue? = (fieldId: string, fieldProperty: string, value: any, tab?: IntegrationTabStructure) => {
        const field = this.getRecordField(fieldId, tab);
        field && (field[fieldProperty] = value);
    };

    getRecordFieldByProperty? = (propertyName: string, propertyValue: any, tab?: IntegrationTabStructure): IntegrationConfigField => {
        if (tab) {
            let field = tab.fields && tab.fields.find(recordField => recordField[propertyName] === propertyValue);
            if (!field) {
                field = tab.recordActions && tab.recordActions.find(recordField => recordField[propertyName] === propertyValue);
            }
            if (!field) {
                (tab as any as IntegrationsSubSection).childSubSections?.forEach(subsection => {
                    if (field) {
                        return;
                    }
                    if (subsection.id === propertyValue) {
                        field = subsection as any;
                    }
                });
            }
            if (field) {
                return field;
            }
        } else {
            let field = this.recordData.fields.find(recordField => recordField[propertyName] === propertyValue);
            if (!field) {
                this.recordData.subSections &&
                    this.recordData.subSections.forEach(section => {
                        !field &&
                            (field = section.fields && section.fields.find(sectionField => sectionField[propertyName] === propertyValue));
                    });
            }
            if (field) {
                return field;
            }
        }
    };

    getRecordFieldFromFields = (fields: IntegrationConfigField[], propertyName: string, propertyValue: any): IntegrationConfigField => {
        return fields?.find(field => field[propertyName] === propertyValue);
    };

    getRecordFieldFromSubSections = (
        subsections: IntegrationsSubSection[],
        propertyName: string,
        propertyValue: any
    ): IntegrationConfigField => {
        let field;
        subsections?.forEach(section => {
            !field && (field = this.getRecordFieldFromFields(section.fields, propertyName, propertyValue));
            if (section.id === propertyValue) {
                field = section as any;
                return;
            }
            if (!field && section.childSubSections?.length > 0) {
                field = this.getRecordFieldFromSubSections(section.childSubSections, propertyName, propertyValue);
            }
            if (!field && section.tabs?.length > 0) {
                field = this.getRecordFieldFromTabs(section.tabs, propertyName, propertyValue);
            }
        });
        return field;
    };

    getRecordFieldFromTabs = (tabs: IntegrationTabStructure[], propertyName: string, propertyValue: any): IntegrationConfigField => {
        let field;
        tabs?.forEach(tab => {
            if (field) {
                return;
            }
            field = this.getRecordFieldFromTab(tab, propertyName, propertyValue);
        });
        return field;
    };

    getRecordFieldFromTab = (tab: IntegrationTabStructure, propertyName: string, propertyValue: any): IntegrationConfigField => {
        let field;
        field = this.getRecordFieldFromFields(tab.fields, propertyName, propertyValue);
        if (!field) {
            field = tab.recordActions && tab.recordActions.find(recordField => recordField[propertyName] === propertyValue);
        }
        if (!field && tab.childSubSections) {
            field = this.getRecordFieldFromSubSections(tab.childSubSections, propertyName, propertyValue);
        }
        return field;
    };

    getRecordFieldParentObjectByProperty? = (propertyName: string, propertyValue: any, tab?: IntegrationTabStructure): any => {
        if (tab) {
            const parentObject = this.getRecordFieldParentObjectFromTab(tab, propertyName, propertyValue);
            if (parentObject) {
                return parentObject;
            }
        } else {
            let parentObject = this.getRecordFieldParentObjectFromFields(
                this.recordData,
                this.recordData.fields,
                propertyName,
                propertyValue
            );
            if (!parentObject) {
                parentObject = this.getRecordFieldParentObjectFromSubSections(this.recordData.subSections, propertyName, propertyValue);
            }
            if (parentObject) {
                return parentObject;
            }
        }
    };

    getRecordFieldParentObjectFromFields = (
        parent: any,
        fields: IntegrationConfigField[],
        propertyName: string,
        propertyValue: any
    ): any => {
        return fields.find(field => field[propertyName] === propertyValue) ? parent : undefined;
    };

    getRecordFieldParentObjectFromSubSections = (subsections: IntegrationsSubSection[], propertyName: string, propertyValue: any): any => {
        let parentObject;
        subsections?.forEach(section => {
            !parentObject &&
                (parentObject = this.getRecordFieldParentObjectFromFields(section, section.fields, propertyName, propertyValue));
            if (!parentObject && section.id === propertyValue) {
                parentObject = section as any;
                return;
            }
            if (!parentObject && section.childSubSections?.length > 0) {
                parentObject = this.getRecordFieldParentObjectFromSubSections(section.childSubSections, propertyName, propertyValue);
            }
            if (!parentObject && section.tabs?.length > 0) {
                parentObject = this.getRecordFieldParentObjectFromTabs(section.tabs, propertyName, propertyValue);
            }
        });
        return parentObject;
    };

    getRecordFieldParentObjectFromTabs = (
        tabs: IntegrationTabStructure[],
        propertyName: string,
        propertyValue: any
    ): IntegrationConfigField => {
        let parentObject;
        tabs?.forEach(tab => {
            if (parentObject) {
                return;
            }
            parentObject = this.getRecordFieldParentObjectFromTab(tab, propertyName, propertyValue);
        });
        return parentObject;
    };

    getRecordFieldParentObjectFromTab = (
        tab: IntegrationTabStructure,
        propertyName: string,
        propertyValue: any
    ): IntegrationConfigField => {
        let parentObject = this.getRecordFieldParentObjectFromFields(tab, tab.fields, propertyName, propertyValue);
        if (!parentObject) {
            parentObject = tab.recordActions?.find(recordField => recordField[propertyName] === propertyValue) ? tab : undefined;
        }
        if (!parentObject && tab.childSubSections) {
            parentObject = this.getRecordFieldParentObjectFromSubSections(tab.childSubSections, propertyName, propertyValue);
        }
        return parentObject;
    };

    getRecordFieldValueByProperty? = (propertyName: string, propertyValue: any): string | boolean | string[] => {
        const field = this.getRecordFieldByProperty(propertyName, propertyValue);
        return field && field.value;
    };

    getMasterField? = (): IntegrationConfigField => {
        const field = this.recordData.fields.find(recordField => recordField.isMasterField);
        if (field) {
            return field;
        }
    };

    canHideField? = (field: IntegrationConfigField, tab?: IntegrationTabStructure, _subSectionRecord?: any) => {
        const masterField = this.getMasterField();
        if (field.show === false) {
            return true;
        }
        const masterValue = masterField && masterField.value;
        let satisfied = false;
        if (field.conditionalDisplay) {
            let satisfiedConditions;
            field.conditionalDisplay &&
                field.conditionalDisplay.forEach(conditionalDisplay => {
                    switch (conditionalDisplay.type) {
                        case 'ALL_MUST_SATISFY':
                            satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId, tab);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length === conditionalDisplay.conditions.length;
                            break;
                        case 'ANY_SATISFY':
                            satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length > 0;
                            break;
                        default:
                            break;
                    }
                });
            if (!satisfied) {
                if (!tab && masterField) {
                    if (masterValue === false) {
                        field.value = field.defaultValue;
                    } else if (masterValue === true) {
                        field.value = undefined;
                    }
                } else if (tab) {
                    if (tab.array) {
                        /** @todo Do nothing for now */
                    } else {
                        field.value = field.value || field.defaultValue || undefined;
                    }
                }
            }
            return !satisfied;
        }
        return satisfied;
    };

    canAssignDefaultValue? = (field: IntegrationConfigField, tab?: IntegrationTabStructure['records'][0]) => {
        let satisfied = false;
        if (field.defaultValueIf) {
            let satisfiedConditions;
            field.defaultValueIf &&
                field.defaultValueIf.forEach(conditionalDefault => {
                    switch (conditionalDefault.type) {
                        case 'ALL_MUST_SATISFY':
                            satisfiedConditions = conditionalDefault.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId, tab as any);
                                let requiredValue = requiredField?.value;
                                if ((requiredField as any as IntegrationsSubSection)?.records) {
                                    requiredValue =
                                        (requiredField as any as IntegrationsSubSection)?.records?.length > 0 ? true : undefined;
                                }
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length === conditionalDefault.conditions.length;
                            break;
                        case 'ANY_SATISFY':
                            satisfiedConditions = conditionalDefault.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length > 0;
                            break;
                        default:
                            break;
                    }
                });
        }
        return satisfied;
    };

    canDisableField? = (field: IntegrationConfigField, tab?: IntegrationTabStructure, _record?: any) => {
        let satisfied = false;
        if (field.disabled) {
            satisfied = true;
        }
        if (!satisfied && field.conditionalDisable) {
            let satisfiedConditions;
            field.conditionalDisable &&
                field.conditionalDisable.forEach(conditionalDisable => {
                    switch (conditionalDisable.type) {
                        case 'ALL_MUST_SATISFY':
                            satisfiedConditions = conditionalDisable.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId, tab);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length === conditionalDisable.conditions.length;
                            break;
                        case 'ANY_SATISFY':
                            satisfiedConditions = conditionalDisable.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId, tab);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length > 0;
                            break;
                        default:
                            break;
                    }
                });
        }
        return satisfied;
    };

    canHideSubSection = (subSection: IntegrationsSubSection, subSectionRecord?: IntegrationsSubSection['records'][0]) => {
        let satisfied = false;
        if (subSection.conditionalDisplay) {
            let satisfiedConditions;
            subSection.conditionalDisplay?.forEach(conditionalDisplay => {
                switch (conditionalDisplay.type) {
                    case 'ALL_MUST_SATISFY':
                        satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, undefined, subSectionRecord);
                            return condition.valueExists
                                ? requiredField?.value !== undefined
                                : condition.requiredValue !== undefined
                                ? condition.requiredValue === requiredField?.value
                                : false;
                        });
                        satisfied = satisfied || satisfiedConditions.length === conditionalDisplay.conditions.length;
                        break;

                    case 'ANY_SATISFY':
                        satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, undefined, subSectionRecord);
                            if (Array.isArray(requiredField)) {
                                return requiredField.some(field => {
                                    let requiredValue = field?.value;

                                    if (field === undefined) {
                                        requiredValue = this[condition.fieldId];
                                    }
                                    return condition.valueExists
                                        ? requiredValue !== undefined
                                        : condition.requiredValue !== undefined
                                        ? condition.requiredValue === requiredValue
                                        : false;
                                });
                            } else {
                                let requiredValue = requiredField?.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            }
                        });
                        satisfied = satisfied || satisfiedConditions.length > 0;
                        break;
                    default:
                        break;
                }
            });
            if (!satisfied) {
                this.applyDefaultValueToSubsection(subSection);
            }
            return !satisfied;
        }
        return satisfied;
    };

    applyDefaultValueToSubsection = (subSection: IntegrationsSubSection) => {
        if (subSection?.array) {
            subSection?.records?.forEach(subSectionRecord => this.applyDefaultValueToRecord(subSectionRecord));
        } else {
            if (subSection?.fields) {
                subSection.fields?.forEach(subSectionField => {
                    subSectionField.value = subSectionField.defaultValue !== undefined ? subSectionField.defaultValue : undefined;
                });
            }
            if (subSection?.childSubSections?.length > 0) {
                subSection?.childSubSections?.forEach(childSubSection => this.applyDefaultValueToSubsection(childSubSection));
            }
            if (subSection?.tabs?.length > 0) {
                subSection.tabs.forEach(tab => this.applyDefaultValueToSubsection(tab as any));
            }
        }
    };

    applyDefaultValueToRecord = (record: IntegrationSubSectionRecord) => {
        record?.fields?.forEach(recordField => {
            recordField.value = recordField.defaultValue !== undefined ? recordField.defaultValue : undefined;
        });
        if (record.childSubSections?.length > 0) {
            record.childSubSections.forEach(childSubSection => this.applyDefaultValueToRecord(childSubSection));
        }
        if ((record as any as IntegrationsSubSection)?.tabs?.length > 0) {
            (record as any as IntegrationsSubSection).tabs.forEach(tab => this.applyDefaultValueToSubsection(tab as any));
        }
    };

    isConditionalArray = (field: IntegrationConfigField) => {
        let satisfied = false;
        let satisfiedConditions: { fieldId: string; requiredValue: boolean; array: boolean }[];
        field.conditionalArray &&
            field.conditionalArray.forEach(conditions => {
                if (satisfied) {
                    return;
                }
                switch (conditions.type) {
                    case 'ALL_MUST_SATISFY':
                        satisfiedConditions = conditions.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, undefined);
                            let requiredValue = requiredField && requiredField.value;
                            if (requiredField === undefined) {
                                requiredValue = this[condition.fieldId];
                            }
                            return condition.requiredValue !== undefined ? condition.requiredValue === requiredValue : false;
                        });
                        satisfied = satisfied || satisfiedConditions.length === conditions.conditions.length;
                        break;
                    case 'ANY_SATISFY':
                        satisfiedConditions = conditions.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId);
                            let requiredValue = requiredField && requiredField.value;
                            if (requiredField === undefined) {
                                requiredValue = this[condition.fieldId];
                            }
                            return condition.requiredValue !== undefined ? condition.requiredValue === requiredValue : false;
                        });
                        satisfied = satisfied || satisfiedConditions.length > 0;
                        break;
                    default:
                        break;
                }
            });
        return satisfied && satisfiedConditions && satisfiedConditions[0] && satisfiedConditions[0].array;
    };
}
